/**
 * Live GPS Tracking Page
 * Shows real-time positions of all company GPS devices on a map
 */

import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  RefreshControl,
  Platform,
  useWindowDimensions,
  Dimensions,
  Alert,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Surface, useTheme, Appbar, Text, TextInput, Button, Portal, Modal, IconButton } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'expo-router';
import { useGPSTracking } from '../../../src/hooks/useGPSTracking';
import gpsTrackingAPI from '../../../src/api/services/gpsTracking';
import GPSMapView from '../../../src/components/GPS/GPSMapView';
import VehiclesSidePanel from '../../../src/components/GPS/VehiclesSidePanel';
import GPSStatistics from '../../../src/components/GPS/GPSStatistics';
import { Sidebar, SIDEBAR_WIDTH } from '../../../src/components/Sidebar';
import { useUser } from '../../../src/hooks/useUser';
import { useProfile } from '../../../src/hooks/useProfile';
import type { LivePosition, GPSDevice } from '../../../src/types/gps';
import Logo from '../../../src/components/Logo';
import { getCompanyLicenses, getCompanyVehicles } from '../../../src/api/apiService';
import { useCompanyContext } from '../../../src/context/CompanyContext';

// Vehicle type based on API response
type Vehicle = {
  id: number;
  registration_number: string;
  fuel_type: string;
  make: string | null;
  model: string | null;
  year: number | null;
  company: number;
  drivers: number[];
};

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');

export default function LiveTrackingPage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const router = useRouter();
  const { width } = useWindowDimensions();
  const isLargeScreen = width > 1024;
  const { selectedCompanyId } = useCompanyContext();
  
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [desktopSidebarVisible, setDesktopSidebarVisible] = useState(isLargeScreen);
  
  const [gpsLicenseState, setGPSLicenseState] = useState<{
    companyId: number | null;
    enabled: boolean;
  }>({ companyId: null, enabled: false });
  const hasGPSLicense = selectedCompanyId !== null
    && gpsLicenseState.companyId === selectedCompanyId
    && gpsLicenseState.enabled;
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingDevices, setLoadingDevices] = useState(false);
  
  // Vehicle panel state
  const [vehiclePanelCollapsed, setVehiclePanelCollapsed] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<LivePosition | null>(null);
  const [showDeviceModal, setShowDeviceModal] = useState(false);
  
  // Vehicle selector modal state
  const [showVehicleSelector, setShowVehicleSelector] = useState(false);
  const [selectedDeviceForLink, setSelectedDeviceForLink] = useState<number | null>(null);
  const [currentVehicleId, setCurrentVehicleId] = useState<number | null>(null);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [vehicleSearchQuery, setVehicleSearchQuery] = useState('');
  
  // All company devices (from API)
  const [allDevices, setAllDevices] = useState<LivePosition[]>([]);
  
  // Device ID mapping (Traccar ID -> Django ID)
  const [deviceIdMap, setDeviceIdMap] = useState<Map<number, number>>(new Map());
  const selectedCompanyIdRef = useRef<number | null>(selectedCompanyId);
  const licenseRequestGenerationRef = useRef(0);
  const vehicleSelectorGenerationRef = useRef(0);

  useEffect(() => {
    selectedCompanyIdRef.current = selectedCompanyId;
  }, [selectedCompanyId]);

  useEffect(() => {
    vehicleSelectorGenerationRef.current += 1;
    setShowVehicleSelector(false);
    setVehicles([]);
    setLoadingVehicles(false);
    setSelectedDeviceForLink(null);
    setCurrentVehicleId(null);
    setVehicleSearchQuery('');
  }, [selectedCompanyId]);
  
  // Sync state
  const [syncingDevice, setSyncingDevice] = useState<number | null>(null);
  
  // Get user data for sidebar
  const { handleLogout, userData } = useUser();
  const { selectedProfile, effectiveProfile, updateProfile, hasOwner, hasDriver } = useProfile(userData?.groups || []);
  
  // Update sidebar visibility based on screen size
  useEffect(() => {
    if (isLargeScreen) {
      setDesktopSidebarVisible(true);
      setSidebarOpen(false);
    } else {
      setDesktopSidebarVisible(false);
    }
  }, [isLargeScreen, width]);

  // Real-time GPS tracking via WebSocket
  const {
    positions,
    isConnected,
    error: wsError,
    requestPositions,
    syncDevice,
  } = useGPSTracking({
    enabled: hasGPSLicense && selectedCompanyId !== null,
    companyId: selectedCompanyId,
  });

  // Merge WebSocket positions with all devices
  // WebSocket positions override static device data when available
  const displayDevices = useMemo(() => {
    if (allDevices.length === 0) {
      return positions; // Use WebSocket data if devices not loaded yet
    }

    // Create map of WebSocket positions by device_id
    const positionsMap = new Map<number, LivePosition>();
    positions.forEach(pos => {
      positionsMap.set(pos.device_id, pos);
    });

    // Merge: use WebSocket data when available, fallback to static device data
    return allDevices.map(device => {
      const wsPosition = positionsMap.get(device.device_id);
      if (wsPosition) {
        // Use real-time data from WebSocket
        return wsPosition;
      }
      // Use static device data (might not have real position yet)
      return device;
    });
  }, [allDevices, positions]);

  const checkLicense = useCallback(async () => {
    const requestGeneration = ++licenseRequestGenerationRef.current;
    const companyId = selectedCompanyId;

    setGPSLicenseState({ companyId, enabled: false });

    // Only check licenses for Owners (non-owners get 404, which is expected)
    if (!hasOwner || companyId === null) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await getCompanyLicenses(companyId);
      if (
        licenseRequestGenerationRef.current !== requestGeneration
        || selectedCompanyIdRef.current !== companyId
      ) {
        return;
      }
      if (response.ok) {
        const gpsLicenses = response.data.summary.gps_licenses || 0;
        setGPSLicenseState({ companyId, enabled: gpsLicenses > 0 });
      } else {
        setGPSLicenseState({ companyId, enabled: false });
      }
    } catch (error) {
      if (
        licenseRequestGenerationRef.current !== requestGeneration
        || selectedCompanyIdRef.current !== companyId
      ) {
        return;
      }
      // Only log actual network/exceptions errors, not HTTP errors (which are in response.status)
      console.error('Failed to check GPS license:', error);
      setGPSLicenseState({ companyId, enabled: false });
    } finally {
      if (
        licenseRequestGenerationRef.current === requestGeneration
        && selectedCompanyIdRef.current === companyId
      ) {
        setLoading(false);
      }
    }
  }, [hasOwner, selectedCompanyId]);

  // Check GPS license on mount or when owner status changes
  useEffect(() => {
    checkLicense();
  }, [checkLicense]);

  // Load device ID mapping when GPS is enabled
  useEffect(() => {
    setAllDevices([]);
    setDeviceIdMap(new Map());
    setSelectedDevice(null);
    setShowDeviceModal(false);

    if (hasGPSLicense && selectedCompanyId !== null) {
      void loadDevices(selectedCompanyId);
    }
  }, [hasGPSLicense, selectedCompanyId]);

  const loadDevices = async (companyId: number | null = selectedCompanyId) => {
    if (companyId === null) {
      return;
    }
    setLoadingDevices(true);
    try {
      const devices = await gpsTrackingAPI.devices.listDevices(companyId);
      const map = new Map<number, number>();
      
      // Convert all devices to LivePosition format (with default location if no position)
      const devicesAsPositions: LivePosition[] = devices.map(device => {
        if (device.traccar_device_id) {
          map.set(device.traccar_device_id, device.id);
        }
        
        // Extract position from GeoJSON format [longitude, latitude]
        let lat = 38.7223; // Default to Lisbon
        let lng = -9.1393;
        if (device.last_position?.coordinates) {
          lng = device.last_position.coordinates[0];
          lat = device.last_position.coordinates[1];
        }
        
        return {
          device_id: device.traccar_device_id || device.id,
          django_device_id: device.id,
          device_label: device.label,
          device_imei: device.imei,
          latitude: lat,
          longitude: lng,
          speed: device.last_speed || 0,
          course: device.last_course || 0,
          timestamp: device.last_position_at || null,
          is_online: device.is_online || false,
          vehicle: device.current_vehicle ? {
            id: device.current_vehicle.id,
            registration_number: device.current_vehicle.registration_number,
            make: device.current_vehicle.make || null,
            model: device.current_vehicle.model || null,
            year: device.current_vehicle.year || null,
          } : null,
          driver: null,
        };
      });
      
      if (selectedCompanyIdRef.current === companyId) {
        setDeviceIdMap(map);
        setAllDevices(devicesAsPositions);
      }
    } catch (error) {
      console.error('Failed to load devices:', error);
    } finally {
      setLoadingDevices(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await loadDevices(); // Reload all devices
      requestPositions(); // Request positions from WebSocket
    } finally {
      setRefreshing(false);
    }
  };

  const handleDevicePress = (position: LivePosition) => {
    setSelectedDevice(position);
    setShowDeviceModal(true);
    syncDevice(position.device_id);
  };

  const handleDeviceListPress = (position: LivePosition) => {
    // On mobile, just select and show on map
    setSelectedDevice(position);
    // Modal will open automatically when marker is tapped on map
  };

  const handleEditVehicleLink = async (traccarDeviceId: number, vehicleId: number | null) => {
    const companyId = selectedCompanyId;
    if (companyId === null || selectedCompanyIdRef.current !== companyId) {
      return;
    }

    // Get Django device ID from map
    const djangoDeviceId = deviceIdMap.get(traccarDeviceId);
    if (!djangoDeviceId) {
      console.error('[GPS] No Django device ID found for device ID:', traccarDeviceId);
      Alert.alert(t('common.error'), t('gps.deviceNotFound'));
      return;
    }

    const requestGeneration = ++vehicleSelectorGenerationRef.current;
    setSelectedDeviceForLink(djangoDeviceId);
    setCurrentVehicleId(vehicleId);
    setVehicleSearchQuery(''); // Reset search when opening modal
    setShowVehicleSelector(true);

    // Load vehicles
    setLoadingVehicles(true);
    try {
      const response = await getCompanyVehicles(1, 100, undefined, undefined, companyId); // Get up to 100 vehicles for selector
      if (
        vehicleSelectorGenerationRef.current !== requestGeneration
        || selectedCompanyIdRef.current !== companyId
      ) {
        return;
      }
      if (response.ok && response.data) {
        // Handle paginated response
        const vehiclesData = Array.isArray(response.data)
          ? response.data
          : (response.data as any)?.results || [];
        setVehicles(vehiclesData);
      } else {
        setVehicles([]);
      }
    } catch (error) {
      if (
        vehicleSelectorGenerationRef.current !== requestGeneration
        || selectedCompanyIdRef.current !== companyId
      ) {
        return;
      }
      console.error('Failed to load vehicles:', error);
      Alert.alert(t('common.error'), t('gps.failedToLoadVehicles'));
      setVehicles([]);
    } finally {
      if (
        vehicleSelectorGenerationRef.current === requestGeneration
        && selectedCompanyIdRef.current === companyId
      ) {
        setLoadingVehicles(false);
      }
    }
  };

  // Filter vehicles based on search query
  const filteredVehicles = Array.isArray(vehicles) ? vehicles.filter(vehicle => {
    const query = vehicleSearchQuery.toLowerCase();
    return (
      vehicle.registration_number?.toLowerCase().includes(query) ||
      vehicle.make?.toLowerCase().includes(query) ||
      vehicle.model?.toLowerCase().includes(query)
    );
  }) : [];

  const renderedVehicleSelectorGeneration = vehicleSelectorGenerationRef.current;

  const handleAssignVehicle = async (vehicleId: number) => {
    const companyId = selectedCompanyId;
    const requestGeneration = renderedVehicleSelectorGeneration;
    if (
      selectedDeviceForLink === null
      || companyId === null
      || selectedCompanyIdRef.current !== companyId
      || vehicleSelectorGenerationRef.current !== requestGeneration
    ) {
      return;
    }

    try {
      await gpsTrackingAPI.devices.assignVehicle(selectedDeviceForLink, vehicleId);
      if (
        vehicleSelectorGenerationRef.current !== requestGeneration
        || selectedCompanyIdRef.current !== companyId
      ) {
        return;
      }
      setShowVehicleSelector(false);
      requestPositions(); // Refresh to get updated vehicle info
      Alert.alert(t('common.success'), t('gps.vehicleAssigned'));
    } catch (error) {
      if (
        vehicleSelectorGenerationRef.current !== requestGeneration
        || selectedCompanyIdRef.current !== companyId
      ) {
        return;
      }
      console.error('Failed to assign vehicle:', error);
      Alert.alert(t('common.error'), t('gps.failedToAssignVehicle'));
    }
  };

  const handleUnassignVehicle = async () => {
    const companyId = selectedCompanyId;
    const requestGeneration = renderedVehicleSelectorGeneration;
    if (
      selectedDeviceForLink === null
      || companyId === null
      || selectedCompanyIdRef.current !== companyId
      || vehicleSelectorGenerationRef.current !== requestGeneration
    ) {
      return;
    }

    try {
      await gpsTrackingAPI.devices.unassignVehicle(selectedDeviceForLink);
      if (
        vehicleSelectorGenerationRef.current !== requestGeneration
        || selectedCompanyIdRef.current !== companyId
      ) {
        return;
      }
      setShowVehicleSelector(false);
      requestPositions(); // Refresh to get updated vehicle info
      Alert.alert(t('common.success'), t('gps.vehicleUnassigned'));
    } catch (error) {
      if (
        vehicleSelectorGenerationRef.current !== requestGeneration
        || selectedCompanyIdRef.current !== companyId
      ) {
        return;
      }
      console.error('Failed to unassign vehicle:', error);
      Alert.alert(t('common.error'), t('gps.failedToUnassignVehicle'));
    }
  };

  const handleForceUpdate = async () => {
    if (!selectedDevice?.django_device_id) return;
    
    setSyncingDevice(selectedDevice.django_device_id);
    try {
      await gpsTrackingAPI.devices.syncPosition(selectedDevice.django_device_id);
      
      // Refresh device data
      await loadDevices();
      requestPositions(); // Request updated positions from WebSocket
      
      // Update selected device if it's still selected
      if (selectedDevice) {
        syncDevice(selectedDevice.device_id);
      }
      
      Alert.alert(t('common.success'), t('gps.deviceDataUpdated'));
    } catch (error) {
      console.error('Failed to sync device:', error);
      Alert.alert(t('common.error'), t('gps.failedToUpdateDevice'));
    } finally {
      setSyncingDevice(null);
    }
  };

  const renderHeader = () => {
    if (isLargeScreen) return null;
    
    return (
      <Appbar.Header style={{ 
        height: SCREEN_HEIGHT * 0.1, 
        backgroundColor: theme.colors.surface,
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1
      }}>
        <Appbar.Action icon="menu" onPress={() => setSidebarOpen(true)} color={theme.colors.onSurface} />
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <Logo 
            size={Platform.OS === 'web' ? 'medium' : 'mobile'} 
            style={{ maxWidth: Platform.OS === 'web' ? 200 : 250 }} 
          />
        </View>
        <View style={{ width: 48 }} />
      </Appbar.Header>
    );
  };

  return (
    <View style={styles.outerContainer}>
      {/* Desktop Sidebar */}
      {desktopSidebarVisible && (
        <Sidebar
          key={`desktop-sidebar-${width}`}
          isOpen={true}
          onClose={() => {}}
          screenWidth={width}
          onLogout={handleLogout}
          isOwner={true}
          isDriver={hasDriver}
          selectedProfile={effectiveProfile}
          onSwitchProfile={hasOwner && hasDriver ? (newProfile: 'Owner' | 'Driver') => { updateProfile(newProfile); } : undefined}
          onRegisterDriver={!hasDriver ? () => router.push('/register/driver?skipLogin=true') : undefined}
        />
      )}

      {/* Main Content */}
      <Surface style={{ flex: 1, backgroundColor: theme.colors.background, ...(desktopSidebarVisible ? { marginLeft: SIDEBAR_WIDTH } : {}) }}>
        {renderHeader()}
        
        <View style={[styles.container, !isLargeScreen && { marginTop: SCREEN_HEIGHT * 0.1 }]}>
          {/* Loading state */}
          {loading && (
            <View style={styles.centerContainer}>
              <ActivityIndicator size="large" color={theme.colors.primary} />
              <Text style={[styles.loadingText, { color: theme.colors.onSurfaceVariant }]}>{t('gps.checkingAccess')}</Text>
            </View>
          )}

          {/* No license state */}
          {!loading && !hasGPSLicense && (
            <View style={styles.noLicenseContainer}>
              <View style={styles.noLicenseContent}>
                <Ionicons name="lock-closed-outline" size={64} color={theme.colors.outline} />
                <Text style={[styles.noLicenseTitle, { color: theme.colors.onSurface }]}>{t('gps.noLicenseTitle')}</Text>
                <Text style={[styles.noLicenseText, { color: theme.colors.onSurfaceVariant }]}>
                  {t('gps.noLicenseMessage')}
                </Text>
                <Text style={[styles.noLicenseText, { color: theme.colors.onSurfaceVariant }]}>
                  {t('gps.noLicenseAction')}
                </Text>
                <Button
                  mode="contained"
                  onPress={() => router.push('/dashboard/manage-licenses')}
                  style={styles.upgradeButton}
                >
                  {t('gps.viewPlans')}
                </Button>
              </View>
            </View>
          )}

          {/* Normal GPS tracking view */}
          {!loading && hasGPSLicense && (
            <View style={styles.contentContainer}>
              {wsError && (
                <View style={[styles.errorBanner, { backgroundColor: theme.colors.errorContainer }]}>
                  <Ionicons name="warning-outline" size={20} color={theme.colors.onErrorContainer} />
                  <Text style={[styles.errorText, { color: theme.colors.onErrorContainer }]}>{wsError}</Text>
                </View>
              )}

              {/* Statistics Dashboard */}
              <GPSStatistics devices={displayDevices} onRefresh={handleRefresh} />

              {/* Main Content Area */}
              <View style={styles.mainContent}>
                {/* GPS Map - Full height */}
                <View style={styles.mapContainer}>
                  <GPSMapView
                    key={`map-${positions.length}`}
                    style={styles.map}
                    positions={positions.filter(p => p.timestamp !== null)}
                    selectedDeviceId={selectedDevice?.device_id}
                    onMarkerPress={handleDevicePress}
                    showsUserLocation={false}
                    region={positions.length > 0 ? undefined : {
                      latitude: 38.7223,
                      longitude: -9.1393,
                      latitudeDelta: 0.5,
                      longitudeDelta: 0.5,
                    }}
                  />
                </View>

                {/* Vehicles Side Panel - Positioned above footer */}
                <View style={styles.panelWrapper}>
                  <VehiclesSidePanel
                    devices={displayDevices}
                    selectedDeviceId={selectedDevice?.device_id}
                    onDevicePress={handleDevicePress}
                    onLocateDevice={(device) => {
                      setSelectedDevice(device);
                      syncDevice(device.device_id);
                    }}
                    onEditVehicleLink={handleEditVehicleLink}
                    isCollapsed={vehiclePanelCollapsed}
                    onToggleCollapse={() => setVehiclePanelCollapsed(!vehiclePanelCollapsed)}
                    isLoading={loadingDevices}
                  />
                </View>
              </View>
            </View>
          )}
        </View>
      </Surface>

      {/* Mobile Sidebar */}
      {!isLargeScreen && (
        <Sidebar
          key={`mobile-sidebar-${width}`}
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          screenWidth={width}
          onLogout={handleLogout}
          isOwner={true}
          isDriver={hasDriver}
          selectedProfile={effectiveProfile}
          onSwitchProfile={hasOwner && hasDriver ? (newProfile: 'Owner' | 'Driver') => { updateProfile(newProfile); } : undefined}
          onRegisterDriver={!hasDriver ? () => router.push('/register/driver?skipLogin=true') : undefined}
        />
      )}

      {/* Vehicle Selector Modal */}
      <Portal>
        <Modal
          visible={showVehicleSelector}
          onDismiss={() => setShowVehicleSelector(false)}
          contentContainerStyle={[
            styles.modalContent,
            { 
              width: Platform.OS === 'web' && width > 768 ? 500 : '90%',
              alignSelf: 'center',
              backgroundColor: theme.colors.surface,
            }
          ]}
        >
          <View style={[styles.modalHeader, { borderBottomColor: theme.colors.outlineVariant }]}>
            <Text variant="titleLarge" style={[styles.modalTitle, { color: theme.colors.onSurface }]}>{t('gps.selectVehicle')}</Text>
            <IconButton icon="close" size={24} onPress={() => setShowVehicleSelector(false)} iconColor={theme.colors.onSurface} />
          </View>

          {/* Vehicle Search */}
          <View style={[styles.modalSearchContainer, { borderBottomColor: theme.colors.outlineVariant }]}>
            <View style={[styles.modalSearchInputContainer, { backgroundColor: theme.colors.surfaceVariant }]}>
              <Ionicons name="search" size={20} color={theme.colors.onSurfaceVariant} style={styles.modalSearchIcon} />
              <TextInput
                mode="flat"
                style={[styles.modalSearchInput, { backgroundColor: 'transparent' }]}
                placeholder={t('gps.searchVehicles')}
                placeholderTextColor={theme.colors.onSurfaceVariant}
                value={vehicleSearchQuery}
                onChangeText={setVehicleSearchQuery}
                autoCapitalize="none"
                autoCorrect={false}
                underlineColor="transparent"
                activeUnderlineColor="transparent"
                textColor={theme.colors.onSurface}
              />
              {vehicleSearchQuery.length > 0 && (
                <IconButton
                  icon="close-circle"
                  size={20}
                  onPress={() => setVehicleSearchQuery('')}
                  iconColor={theme.colors.onSurfaceVariant}
                />
              )}
            </View>
          </View>

          {loadingVehicles ? (
            <View style={styles.modalLoading}>
              <ActivityIndicator size="large" color={theme.colors.primary} />
              <Text style={[styles.loadingText, { color: theme.colors.onSurfaceVariant }]}>{t('common.loading')}</Text>
            </View>
          ) : (
            <ScrollView style={styles.vehicleList}>
              {currentVehicleId && (
                <Button
                  mode="contained-tonal"
                  onPress={handleUnassignVehicle}
                  style={styles.unassignButton}
                  icon="delete-outline"
                  buttonColor={theme.colors.errorContainer}
                  textColor={theme.colors.onErrorContainer}
                >
                  {t('gps.unassignVehicle')}
                </Button>
              )}

              {filteredVehicles.length === 0 ? (
                <View style={styles.noVehiclesContainer}>
                  <Ionicons name="car-outline" size={48} color={theme.colors.outline} />
                  <Text style={[styles.noVehiclesText, { color: theme.colors.onSurfaceVariant }]}>
                    {vehicleSearchQuery ? t('gps.noVehiclesFound') : t('gps.noVehiclesAvailable')}
                  </Text>
                </View>
              ) : (
                filteredVehicles.map((vehicle) => (
                  <TouchableOpacity
                    key={vehicle.id}
                    style={[
                      styles.vehicleItem,
                      { borderBottomColor: theme.colors.outlineVariant },
                      currentVehicleId === vehicle.id && { backgroundColor: theme.colors.primaryContainer },
                    ]}
                    onPress={() => handleAssignVehicle(vehicle.id)}
                  >
                    <View style={styles.vehicleItemContent}>
                      <Ionicons 
                        name="car-sport-outline" 
                        size={24} 
                        color={currentVehicleId === vehicle.id ? theme.colors.primary : theme.colors.onSurfaceVariant} 
                      />
                      <View style={styles.vehicleItemInfo}>
                        <Text style={[styles.vehicleRegistration, { color: theme.colors.onSurface }]}>
                          {vehicle.registration_number}
                        </Text>
                        {(vehicle.make || vehicle.model) && (
                          <Text style={[styles.vehicleModel, { color: theme.colors.onSurfaceVariant }]}>
                            {vehicle.make} {vehicle.model}
                          </Text>
                        )}
                      </View>
                    </View>
                    {currentVehicleId === vehicle.id && (
                      <Ionicons name="checkmark-circle" size={24} color={theme.colors.primary} />
                    )}
                  </TouchableOpacity>
                ))
              )}
            </ScrollView>
          )}
        </Modal>
      </Portal>

      {/* Device Details Modal */}
      <Portal>
        <Modal
          visible={showDeviceModal && selectedDevice !== null}
          onDismiss={() => setShowDeviceModal(false)}
          contentContainerStyle={[
            styles.modalContent, 
            { 
              maxHeight: SCREEN_HEIGHT * 0.8,
              width: Platform.OS === 'web' && width > 768 ? 500 : '90%',
              alignSelf: 'center',
              backgroundColor: theme.colors.surface,
            }
          ]}
        >
          <View style={[styles.modalHeader, { borderBottomColor: theme.colors.outlineVariant }]}>
            <Text variant="titleLarge" style={[styles.modalTitle, { color: theme.colors.onSurface }]}>
              {selectedDevice?.device_label}
            </Text>
            <IconButton icon="close" size={24} onPress={() => setShowDeviceModal(false)} iconColor={theme.colors.onSurface} />
          </View>

          <ScrollView style={{ flex: 1 }}>
            {/* Status */}
            <View style={[styles.detailSection, { borderBottomColor: theme.colors.outlineVariant }]}>
              <View style={styles.detailRow}>
                <View style={[
                  styles.statusDotLarge,
                  { backgroundColor: selectedDevice?.is_online ? theme.colors.primary : theme.colors.outline }
                ]} />
                <Text style={[styles.detailStatusText, { color: theme.colors.onSurface }]}>
                  {selectedDevice?.is_online ? t('gps.online') : t('gps.offline')}
                </Text>
              </View>
            </View>

            {/* Device Info */}
            <View style={[styles.detailSection, { borderBottomColor: theme.colors.outlineVariant }]}>
              <Text style={[styles.detailSectionTitle, { color: theme.colors.onSurfaceVariant }]}>{t('gps.deviceInfo')}</Text>
              <View style={styles.detailRow}>
                <Ionicons name="barcode-outline" size={16} color={theme.colors.onSurfaceVariant} />
                <Text style={[styles.detailLabel, { color: theme.colors.onSurfaceVariant }]}>{t('gps.imei')}:</Text>
                <Text style={[styles.detailValue, { color: theme.colors.onSurface }]}>{selectedDevice?.device_imei}</Text>
              </View>
            </View>

            {/* Vehicle Info */}
            {selectedDevice?.vehicle && (
              <View style={[styles.detailSection, { borderBottomColor: theme.colors.outlineVariant }]}>
                <Text style={[styles.detailSectionTitle, { color: theme.colors.onSurfaceVariant }]}>{t('gps.vehicle')}</Text>
                <View style={styles.detailRow}>
                  <Ionicons name="car-sport" size={16} color={theme.colors.primary} />
                  <Text style={[styles.detailLabel, { color: theme.colors.onSurfaceVariant }]}>{t('gps.registration')}:</Text>
                  <Text style={[styles.detailValue, { color: theme.colors.primary, fontWeight: '700' }]}>
                    {selectedDevice.vehicle.registration_number}
                  </Text>
                </View>
                {(selectedDevice.vehicle.make || selectedDevice.vehicle.model) && (
                  <View style={styles.detailRow}>
                    <View style={{ width: 16 }} />
                    <Text style={[styles.detailLabel, { color: theme.colors.onSurfaceVariant }]}>{t('gps.makeModel')}:</Text>
                    <Text style={[styles.detailValue, { color: theme.colors.onSurface }]}>
                      {selectedDevice.vehicle.make} {selectedDevice.vehicle.model}
                      {selectedDevice.vehicle.year && ` (${selectedDevice.vehicle.year})`}
                    </Text>
                  </View>
                )}
                <Button
                  mode="contained-tonal"
                  onPress={() => {
                    setShowDeviceModal(false);
                    handleEditVehicleLink(
                      selectedDevice.device_id,
                      selectedDevice.vehicle?.id || null
                    );
                  }}
                  style={styles.editVehicleButton}
                  icon="pencil-outline"
                >
                  {t('gps.editVehicle')}
                </Button>
              </View>
            )}

            {!selectedDevice?.vehicle && (
              <View style={[styles.detailSection, { borderBottomColor: theme.colors.outlineVariant }]}>
                <Text style={[styles.detailSectionTitle, { color: theme.colors.onSurfaceVariant }]}>{t('gps.vehicle')}</Text>
                <Text style={[styles.noVehicleText, { color: theme.colors.onSurfaceVariant }]}>{t('gps.noVehicleLinked')}</Text>
                <Button
                  mode="contained-tonal"
                  onPress={() => {
                    if (!selectedDevice) return;
                    setShowDeviceModal(false);
                    handleEditVehicleLink(selectedDevice.device_id, null);
                  }}
                  style={styles.linkVehicleButton}
                  icon="plus-circle-outline"
                >
                  {t('gps.linkVehicle')}
                </Button>
              </View>
            )}

            {/* Location Data */}
            <View style={[styles.detailSection, { borderBottomColor: theme.colors.outlineVariant }]}>
              <Text style={[styles.detailSectionTitle, { color: theme.colors.onSurfaceVariant }]}>{t('gps.locationData')}</Text>
              
              <View style={styles.detailRow}>
                <Ionicons name="speedometer-outline" size={16} color={theme.colors.onSurfaceVariant} />
                <Text style={[styles.detailLabel, { color: theme.colors.onSurfaceVariant }]}>{t('gps.speed')}:</Text>
                <Text style={[styles.detailValue, { color: theme.colors.onSurface }]}>
                  {(selectedDevice?.speed || 0).toFixed(1)} km/h
                </Text>
              </View>

              <View style={styles.detailRow}>
                <Ionicons name="compass-outline" size={16} color={theme.colors.onSurfaceVariant} />
                <Text style={[styles.detailLabel, { color: theme.colors.onSurfaceVariant }]}>{t('gps.course')}:</Text>
                <Text style={[styles.detailValue, { color: theme.colors.onSurface }]}>
                  {(selectedDevice?.course || 0).toFixed(0)}°
                </Text>
              </View>

              <View style={styles.detailRow}>
                <Ionicons name="time-outline" size={16} color={theme.colors.onSurfaceVariant} />
                <Text style={[styles.detailLabel, { color: theme.colors.onSurfaceVariant }]}>{t('gps.lastUpdate')}:</Text>
                <Text style={[styles.detailValue, { color: theme.colors.onSurface }]}>
                  {selectedDevice?.timestamp
                    ? new Date(selectedDevice.timestamp).toLocaleString()
                    : t('gps.noData')}
                </Text>
              </View>

              <View style={styles.detailRow}>
                <Ionicons name="location-outline" size={16} color={theme.colors.onSurfaceVariant} />
                <Text style={[styles.detailLabel, { color: theme.colors.onSurfaceVariant }]}>{t('gps.coordinates')}:</Text>
                <Text style={[styles.detailValue, { color: theme.colors.onSurface, fontFamily: Platform.OS === 'web' ? 'monospace' : undefined }]}>
                  {selectedDevice?.latitude.toFixed(6)}, {selectedDevice?.longitude.toFixed(6)}
                </Text>
              </View>
            </View>

            {/* Force Update Button */}
            {selectedDevice?.django_device_id && (
              <View style={[styles.detailSection, { borderBottomColor: 'transparent' }]}>
                <Button
                  mode="contained"
                  onPress={handleForceUpdate}
                  disabled={syncingDevice === selectedDevice.django_device_id}
                  loading={syncingDevice === selectedDevice.django_device_id}
                  style={styles.forceUpdateButton}
                  icon="refresh"
                >
                  {syncingDevice === selectedDevice.django_device_id 
                    ? t('gps.updating') 
                    : t('gps.forceUpdate')}
                </Button>
                <Text style={[styles.forceUpdateNote, { color: theme.colors.onSurfaceVariant }]}>
                  {t('gps.forceUpdateNote')}
                </Text>
              </View>
            )}

          </ScrollView>
        </Modal>
      </Portal>
    </View>
  );
}

const styles = StyleSheet.create({
  outerContainer: {
    flex: 1,
    flexDirection: 'row',
  },
  container: {
    flex: 1,
  },
  contentContainer: {
    flex: 1,
  },
  mainContent: {
    flex: 1,
    flexDirection: 'row',
    position: 'relative',
  },
  panelWrapper: {
    position: 'relative',
    ...Platform.select({
      web: {
        zIndex: 100,
      },
      ios: {
        zIndex: 100,
      },
      android: {
        elevation: 20,
      },
    }),
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  header: {
    padding: 16,
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomWidth: 1,
    gap: 12,
  },
  menuButton: {
    padding: 4,
  },
  headerContent: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: '700',
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 4,
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 6,
  },
  statusText: {
    fontSize: 14,
  },
  deviceCount: {
    fontSize: 14,
    marginLeft: 4,
  },
  refreshButton: {
    padding: 8,
  },
  errorBanner: {
    padding: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  errorText: {
    fontSize: 14,
    flex: 1,
  },
  mapContainer: {
    flex: 1,
  },
  map: {
    flex: 1,
    width: '100%',
    height: '100%',
  },
  sectionTitle: {
    fontSize: 17,
    fontWeight: '600',
    paddingHorizontal: 16,
    marginBottom: 12,
  },
  loadingText: {
    fontSize: 16,
    marginTop: 16,
  },
  noLicenseContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  noLicenseContent: {
    alignItems: 'center',
    maxWidth: 500,
  },
  noLicenseTitle: {
    fontSize: 24,
    fontWeight: '700',
    marginTop: 16,
    textAlign: 'center',
  },
  noLicenseText: {
    fontSize: 16,
    marginTop: 8,
    textAlign: 'center',
    paddingHorizontal: 20,
  },
  upgradeButton: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
    marginTop: 24,
  },
  upgradeButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    borderRadius: 12,
    maxWidth: 500,
    maxHeight: '80%',
    padding: 0,
    overflow: 'hidden',
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.3,
        shadowRadius: 8,
      },
      android: {
        elevation: 8,
      },
      web: {
        boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
      },
    }),
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
  },
  modalSearchContainer: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  modalSearchInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 10,
    paddingHorizontal: 12,
    height: 48,
  },
  modalSearchIcon: {
    marginRight: 8,
  },
  modalSearchInput: {
    flex: 1,
    fontSize: 16,
  },
  modalClearButton: {
    padding: 4,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '700',
  },
  modalLoading: {
    padding: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  vehicleList: {
    maxHeight: 400,
  },
  unassignButton: {
    margin: 16,
  },
  unassignButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  noVehiclesContainer: {
    padding: 40,
    alignItems: 'center',
  },
  noVehiclesText: {
    fontSize: 16,
    marginTop: 12,
    textAlign: 'center',
  },
  vehicleItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
  },
  vehicleItemSelected: {
  },
  vehicleItemContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
  },
  vehicleItemInfo: {
    flex: 1,
  },
  vehicleRegistration: {
    fontSize: 17,
    fontWeight: '600',
  },
  vehicleModel: {
    fontSize: 14,
    marginTop: 2,
  },
  detailSection: {
    padding: 16,
    borderBottomWidth: 1,
  },
  detailSectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 12,
    textTransform: 'uppercase',
  },
  detailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    gap: 8,
  },
  detailLabel: {
    fontSize: 14,
    minWidth: 100,
  },
  detailValue: {
    fontSize: 14,
    flex: 1,
  },
  statusDotLarge: {
    width: 12,
    height: 12,
    borderRadius: 6,
  },
  detailStatusText: {
    fontSize: 16,
    fontWeight: '600',
  },
  editVehicleButton: {
    marginTop: 12,
  },
  editVehicleText: {
    fontSize: 14,
    fontWeight: '600',
  },
  linkVehicleButton: {
    marginTop: 8,
  },
  linkVehicleText: {
    fontSize: 14,
    fontWeight: '600',
  },
  noVehicleText: {
    fontSize: 14,
    fontStyle: 'italic',
  },
  forceUpdateButton: {
    marginTop: 8,
  },
  forceUpdateText: {
    fontSize: 15,
    fontWeight: '600',
  },
  forceUpdateNote: {
    fontSize: 12,
    textAlign: 'center',
    marginTop: 6,
    fontStyle: 'italic',
  },
});

